import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";

const PrivacyWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: radial-gradient(circle at top, #1d1d1f, #050505 80%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  box-sizing: border-box;
`;

const PrivacyCard = styled.div`
  width: min(920px, 100%);
  background: #ffffff;
  border-radius: 32px;
  padding: 48px;
  box-shadow: 0 25px 55px rgba(0, 0, 0, 0.35);
  color: #111;
  font-family: "Inter", "DM Sans", sans-serif;

  @media (max-width: 500px) {
    padding: 20px;
    border-radius: 16px;
  }
`;

const PrivacyTitle = styled.h1`
  font-size: 36px;
  margin-bottom: 6px;
`;

const PrivacySubtitle = styled.p`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 22px;
  margin-top: 24px;
  margin-bottom: 12px;
`;

const SectionText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  margin-bottom: 14px;
  color: #1b1b1f;
`;

const BulletList = styled.ul`
  margin-left: 20px;
  margin-bottom: 12px;
  color: #1b1b1f;
`;

const BulletItem = styled.li`
  margin-bottom: 6px;
`;

const ContactLink = styled.a`
  color: #050505;
  font-weight: 600;
  text-decoration: underline;
`;

const ReturnButton = styled(Link)`
  margin-top: 24px;
  background: #050505;
  color: #fff;
  border-radius: 999px;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.2s ease;
  &:hover,
  &:focus-visible {
    opacity: 0.8;
  }
`;

const PrivacyPage = () => (
  <PrivacyWrapper>
    <PrivacyCard>
      <PrivacyTitle>Politique de confidentialité – GeekVibz App.</PrivacyTitle>
      <PrivacySubtitle>Dernière mise à jour : 2025</PrivacySubtitle>

      <SectionText>
        GeekVibz est une application mobile éducative développée par GeekVibz
        (France). Notre objectif est d’offrir une expérience d’apprentissage
        ludique, tout en respectant votre vie privée. Cette politique explique
        quelles données nous collectons, comment nous les utilisons et les
        droits que vous possédez.
      </SectionText>

      <SectionTitle>1. Données collectées</SectionTitle>
      <SectionText>
        Nous collectons uniquement les informations nécessaires au
        fonctionnement de l’application :
      </SectionText>
      <BulletList>
        <BulletItem>Pseudonyme ou nom renseigné dans l’application.</BulletItem>
        <BulletItem>Adresse e-mail (si création de compte).</BulletItem>
        <BulletItem>
          Progression : points d’XP, contenus débloqués, score des quiz.
        </BulletItem>
        <BulletItem>
          Liste d'amis (pseudonymes uniquement, si l’utilisateur accepte
          d’ajouter des amis).
        </BulletItem>
      </BulletList>
      <SectionText>
        <strong>Aucune donnée sensible n’est collectée.</strong> Nous ne vendons
        aucune donnée personnelle.
      </SectionText>

      <SectionTitle>2. Utilisation des données</SectionTitle>
      <SectionText>
        Les données collectées sont utilisées uniquement pour :
      </SectionText>
      <BulletList>
        <BulletItem>Créer et gérer votre compte.</BulletItem>
        <BulletItem>
          Mémoriser votre progression et débloquer du contenu.
        </BulletItem>
        <BulletItem>Proposer des fonctionnalités sociales (amis).</BulletItem>
        <BulletItem>Sécuriser l’accès et améliorer l’application.</BulletItem>
      </BulletList>

      <SectionTitle>3. Partage des données</SectionTitle>
      <SectionText>
        Aucune donnée n’est vendue ou partagée à des fins commerciales. Les
        informations peuvent être hébergées par des services techniques tels que
        Firebase, uniquement pour permettre le bon fonctionnement de
        l’application.
      </SectionText>

      <SectionTitle>4. Durée de conservation</SectionTitle>
      <SectionText>
        Les données sont conservées tant que votre compte est actif. Vous pouvez
        demander leur suppression à tout moment.
      </SectionText>

      <SectionTitle>5. Fonctionnalité Amis</SectionTitle>
      <SectionText>
        GeekVibz permet aux utilisateurs d’ajouter des amis au sein de
        l’application. Seuls les pseudonymes sont visibles. Aucune information
        privée (email, numéro, vraie identité) n'est partagée. Si vous ne
        souhaitez pas utiliser cette fonctionnalité, vous pouvez choisir de ne
        pas ajouter d’amis.
      </SectionText>

      <SectionTitle>6. Utilisateurs mineurs</SectionTitle>
      <SectionText>
        L’application peut être utilisée par des mineurs. Nous encourageons les
        parents ou responsables légaux à superviser l’utilisation de GeekVibz.
      </SectionText>

      <SectionTitle>7. Cookies</SectionTitle>
      <SectionText>
        Aucun cookie marketing n’est utilisé. Seuls des cookies techniques
        peuvent être utilisés par les services et hébergements techniques.
      </SectionText>

      <SectionTitle>8. Vos droits (RGPD)</SectionTitle>
      <SectionText>Vous pouvez demander :</SectionText>
      <BulletList>
        <BulletItem>L’accès à vos données.</BulletItem>
        <BulletItem>La modification de vos données.</BulletItem>
        <BulletItem>La suppression de votre compte.</BulletItem>
      </BulletList>
      <SectionText>
        <strong>Contact :</strong>{" "}
        <ContactLink href="mailto:youssefdevweb@gmail.com">
          youssefdevweb@gmail.com
        </ContactLink>
      </SectionText>

      <SectionTitle>9. Sécurité</SectionTitle>
      <SectionText>
        Nous appliquons des mesures de protection adaptées pour sécuriser les
        comptes et les données.
      </SectionText>

      <SectionTitle>10. Contact</SectionTitle>
      <SectionText>
        Pour toute question liée à la confidentialité, vous pouvez nous
        contacter par email :{" "}
        <ContactLink href="mailto:youssefdevweb@gmail.com">
          youssefdevweb@gmail.com
        </ContactLink>
      </SectionText>

      <ReturnButton to="/">Revenir à la page principale</ReturnButton>
    </PrivacyCard>
  </PrivacyWrapper>
);

export default PrivacyPage;
